import { useState, useEffect } from 'react';

// Standard breakpoints
const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
};

export type Breakpoint = keyof typeof BREAKPOINTS;

function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.xxl) return 'xxl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

export function useBreakpoint(): { breakpoint: Breakpoint; width: number } {
  const [width, setWidth] = useState<number>(() => window.innerWidth);
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => getBreakpoint(window.innerWidth));

  useEffect(() => {
    let animationFrame: number | null = null;
    let lastWidth = window.innerWidth;

    function checkWidth() {
      const currentWidth = window.innerWidth;
      if (currentWidth !== lastWidth) {
        lastWidth = currentWidth;
        setWidth(currentWidth);
        setBreakpoint(getBreakpoint(currentWidth));
      }
      animationFrame = window.requestAnimationFrame(checkWidth);
    }

    function handleResize() {
      setWidth(window.innerWidth);
      setBreakpoint(getBreakpoint(window.innerWidth));
    }

    window.addEventListener('resize', handleResize);
    animationFrame = window.requestAnimationFrame(checkWidth);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return { breakpoint, width };
}
